import React, { useEffect, useState } from "react";
import axios from "axios";
import { Trash2, Save, Plus, LogOut, ChevronDown, ChevronRight } from "lucide-react";
import { SiteContentEditor } from "../components/admin/SiteContentEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "aw_admin_token";

const empty = { brand: "", model: "", reference: "", description: "", price_chf: "", image_url: "", images_text: "", chrono24_url: "" };

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [loginErr, setLoginErr] = useState("");
  const [watches, setWatches] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [draft, setDraft] = useState({});
  const [creating, setCreating] = useState(false);
  const [newDraft, setNewDraft] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("catalog");

  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => m.remove();
  }, []);

  const auth = { headers: { "X-Admin-Token": token } };

  const load = async () => {
    const r = await axios.get(`${API}/watches`);
    setWatches(r.data || []);
  };

  useEffect(() => {
    if (token) load();
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoginErr("");
    try {
      const r = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, r.data.token);
      setToken(r.data.token);
    } catch (err) {
      setLoginErr("Falsches Passwort");
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  const startEdit = (w) => {
    setOpenId(w.id);
    setDraft({
      ...w,
      price_chf: w.price_chf ?? "",
      images_text: (w.images || []).join("\n"),
    });
  };

  const save = async (id) => {
    setBusy(true);
    try {
      const payload = {
        brand: draft.brand,
        model: draft.model,
        reference: draft.reference || null,
        description: draft.description || null,
        price_chf: draft.price_chf === "" ? null : Number(draft.price_chf),
        image_url: draft.image_url || null,
        images: (draft.images_text || "").split("\n").map((s) => s.trim()).filter(Boolean),
        chrono24_url: draft.chrono24_url || null,
      };
      await axios.put(`${API}/admin/watches/${id}`, payload, auth);
      await load();
      setOpenId(null);
    } catch (e) {
      alert("Speichern fehlgeschlagen: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Diese Uhr wirklich löschen?")) return;
    await axios.delete(`${API}/admin/watches/${id}`, auth);
    await load();
  };

  const create = async () => {
    setBusy(true);
    try {
      const payload = {
        brand: newDraft.brand,
        model: newDraft.model,
        reference: newDraft.reference || null,
        description: newDraft.description || null,
        price_chf: newDraft.price_chf === "" ? null : Number(newDraft.price_chf),
        image_url: newDraft.image_url || null,
        images: (newDraft.images_text || "").split("\n").map((s) => s.trim()).filter(Boolean),
        chrono24_url: newDraft.chrono24_url || null,
      };
      if (!payload.brand || !payload.model) {
        alert("Marke und Modell sind Pflichtfelder.");
        return;
      }
      await axios.post(`${API}/admin/watches`, payload, auth);
      setNewDraft(empty);
      setCreating(false);
      await load();
    } catch (e) {
      alert("Erstellen fehlgeschlagen: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <form onSubmit={login} className="w-full max-w-sm" data-testid="admin-login-form">
          <h1 className="font-serif-aw text-3xl mb-2">Admin</h1>
          <p className="font-sans-aw text-sm text-black/60 mb-8">Alpha Watch · Atelier-Verwaltung</p>
          <label className="font-sans-aw text-[10px] tracking-luxury uppercase text-black/60 block mb-2">Passwort</label>
          <input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full border border-black/20 focus:border-black px-4 py-3 font-sans-aw text-sm outline-none mb-4"
            autoFocus
          />
          {loginErr && <p className="text-sm text-red-600 mb-3 font-sans-aw">{loginErr}</p>}
          <button data-testid="admin-login-btn" type="submit" className="w-full bg-black text-white font-sans-aw text-[11px] tracking-luxury uppercase py-3 hover:bg-black/85">
            Anmelden
          </button>
        </form>
      </div>
    );
  }

  const inputCls = "w-full border border-black/15 focus:border-black px-3 py-2 font-sans-aw text-sm outline-none";
  const labelCls = "font-sans-aw text-[10px] tracking-luxury uppercase text-black/60 block mb-1.5";

  const Field = ({ label, value, onChange, type = "text", placeholder, textarea, rows = 2 }) => (
    <div>
      <label className={labelCls}>{label}</label>
      {textarea ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={rows} className={inputCls} />
      ) : (
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={inputCls} />
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-black/10 px-6 md:px-10 py-5 flex items-center justify-between sticky top-0 bg-white z-30">
        <div>
          <h1 className="font-serif-aw text-2xl">Admin · {tab === "catalog" ? "Katalog" : "Website-Texte"}</h1>
          <div className="flex gap-4 mt-2 font-sans-aw text-[10px] tracking-luxury uppercase">
            <button data-testid="admin-tab-catalog" onClick={() => setTab("catalog")} className={tab === "catalog" ? "text-black border-b border-black" : "text-black/40 hover:text-black"}>Katalog · {watches.length} Uhren</button>
            <button data-testid="admin-tab-site" onClick={() => setTab("site")} className={tab === "site" ? "text-black border-b border-black" : "text-black/40 hover:text-black"}>Website-Texte</button>
          </div>
        </div>
        <div className="flex gap-2">
          <a href="/" className="font-sans-aw text-[10px] tracking-luxury uppercase border border-black/20 hover:border-black px-3 py-2">Zurück zur Website</a>
          {tab === "catalog" && <button data-testid="admin-add-btn" onClick={() => setCreating((v) => !v)} className="font-sans-aw text-[10px] tracking-luxury uppercase bg-black text-white px-3 py-2 inline-flex items-center gap-1.5">
            <Plus size={13} /> Neue Uhr
          </button>}
          <button onClick={logout} className="font-sans-aw text-[10px] tracking-luxury uppercase border border-black/20 px-3 py-2 inline-flex items-center gap-1.5">
            <LogOut size={13} /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-10 py-10">
        {tab === "site" ? <SiteContentEditor token={token} /> : <>
        {creating && (
          <div className="border border-black/15 p-6 mb-10" data-testid="admin-create-form">
            <h2 className="font-serif-aw text-xl mb-5">Neue Uhr hinzufügen</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Marke *" value={newDraft.brand} onChange={(v) => setNewDraft({ ...newDraft, brand: v })} placeholder="z.B. Rolex" />
              <Field label="Modell *" value={newDraft.model} onChange={(v) => setNewDraft({ ...newDraft, model: v })} placeholder="z.B. Submariner" />
              <Field label="Referenz" value={newDraft.reference} onChange={(v) => setNewDraft({ ...newDraft, reference: v })} />
              <Field label="Preis CHF" type="number" value={newDraft.price_chf} onChange={(v) => setNewDraft({ ...newDraft, price_chf: v })} placeholder="leer = auf Anfrage" />
              <div className="md:col-span-2"><Field label="Beschreibung" textarea value={newDraft.description} onChange={(v) => setNewDraft({ ...newDraft, description: v })} /></div>
              <Field label="Haupt-Bild URL" value={newDraft.image_url} onChange={(v) => setNewDraft({ ...newDraft, image_url: v })} placeholder="https://..." />
              <Field label="Chrono24 Link" value={newDraft.chrono24_url} onChange={(v) => setNewDraft({ ...newDraft, chrono24_url: v })} placeholder="https://www.chrono24.ch/..." />
              <div className="md:col-span-2"><Field label="Weitere Bild-URLs (eine pro Zeile)" textarea rows={3} value={newDraft.images_text} onChange={(v) => setNewDraft({ ...newDraft, images_text: v })} placeholder="https://...&#10;https://..." /></div>
            </div>
            <div className="mt-5 flex gap-3">
              <button onClick={create} disabled={busy} className="font-sans-aw text-[10px] tracking-luxury uppercase bg-black text-white px-4 py-2 inline-flex items-center gap-1.5">
                <Save size={13} /> Speichern
              </button>
              <button onClick={() => { setCreating(false); setNewDraft(empty); }} className="font-sans-aw text-[10px] tracking-luxury uppercase border border-black/20 px-4 py-2">Abbrechen</button>
            </div>
          </div>
        )}

        <ul className="divide-y divide-black/10 border-y border-black/10">
          {watches.map((w) => {
            const open = openId === w.id;
            return (
              <li key={w.id} data-testid={`admin-row-${w.id}`}>
                <div className="flex items-center gap-4 py-4">
                  <img src={(w.images && w.images[0]) || w.image_url} alt="" className="w-16 h-16 object-cover aw-grayscale" />
                  <div className="flex-1 min-w-0">
                    <div className="font-sans-aw text-[10px] tracking-luxury uppercase text-black/50">{w.brand}</div>
                    <div className="font-serif-aw text-lg truncate">{w.model} {w.reference && <span className="text-black/50 text-sm">· {w.reference}</span>}</div>
                  </div>
                  <div className="font-sans-aw text-sm text-black/70 w-28 text-right">
                    {w.price_chf != null ? `CHF ${new Intl.NumberFormat("de-CH").format(w.price_chf)}` : "Auf Anfrage"}
                  </div>
                  <button onClick={() => (open ? setOpenId(null) : startEdit(w))} className="font-sans-aw text-[10px] tracking-luxury uppercase border border-black/15 hover:border-black px-3 py-2 inline-flex items-center gap-1.5">
                    {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />} Bearbeiten
                  </button>
                  <button onClick={() => del(w.id)} className="text-black/50 hover:text-black p-2" aria-label="Löschen"><Trash2 size={16} /></button>
                </div>

                {open && (
                  <div className="bg-[#fafafa] border-t border-black/5 p-5 mb-2">
                    <div className="grid md:grid-cols-2 gap-4">
                      <Field label="Marke" value={draft.brand || ""} onChange={(v) => setDraft({ ...draft, brand: v })} />
                      <Field label="Modell" value={draft.model || ""} onChange={(v) => setDraft({ ...draft, model: v })} />
                      <Field label="Referenz" value={draft.reference || ""} onChange={(v) => setDraft({ ...draft, reference: v })} />
                      <Field label="Preis CHF" type="number" value={draft.price_chf} onChange={(v) => setDraft({ ...draft, price_chf: v })} />
                      <div className="md:col-span-2"><Field label="Beschreibung" textarea value={draft.description || ""} onChange={(v) => setDraft({ ...draft, description: v })} /></div>
                      <Field label="Haupt-Bild URL" value={draft.image_url || ""} onChange={(v) => setDraft({ ...draft, image_url: v })} />
                      <Field label="Chrono24 Link" value={draft.chrono24_url || ""} onChange={(v) => setDraft({ ...draft, chrono24_url: v })} />
                      <div className="md:col-span-2"><Field label="Weitere Bild-URLs (eine pro Zeile)" textarea rows={4} value={draft.images_text || ""} onChange={(v) => setDraft({ ...draft, images_text: v })} /></div>
                    </div>
                    <div className="mt-4 flex gap-3">
                      <button onClick={() => save(w.id)} disabled={busy} className="font-sans-aw text-[10px] tracking-luxury uppercase bg-black text-white px-4 py-2 inline-flex items-center gap-1.5">
                        <Save size={13} /> Speichern
                      </button>
                      <button onClick={() => setOpenId(null)} className="font-sans-aw text-[10px] tracking-luxury uppercase border border-black/20 px-4 py-2">Abbrechen</button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        </>}
      </main>
    </div>
  );
};

export default Admin;
